import moment from "moment";
import { cnpj, cpf } from "cpf-cnpj-validator";

export const formatNumeral = (n: any): string => {
  if (!n) return "-";

  return Number(n).toLocaleString();
};

export const formatPercentage = (v: any) => {
  if (!v) return "--";

  const valorFormatado = parseFloat(v).toFixed(2);

  return `${valorFormatado}%`;
};

export const formatPrice = (v: any, currency = true) => {
  if (typeof v === "string") {
    v = v.replace(",", ".");
  }

  if (!v && v !== 0) return v;

  if (currency) {
    v = String(v);

    return Number(v).toLocaleString("pt-br", {
      style: "currency",
      currency: "BRL",
    });
  }

  const valorFormatado = parseFloat(v).toFixed(2);

  if (isNaN(Number(valorFormatado))) {
    return v;
  }

  const precoFormatado = valorFormatado.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  return precoFormatado;
};

export const formatDate = (v: string) => moment(v).format("DD/MM/YYYY");
export const formatHour = (v: string) => moment(v).format("HH:mm");

export const formatCpfCnpj = (v: string) =>
  (cpf.isValid(v) && cpf.format(v)) || (cnpj.isValid(v) && cnpj.format(v)) || v;

export const validateCpfCnpj = (v: string) => cpf.isValid(v) || cnpj.isValid(v);

export const formatCpf = (v: string) => cpf.format(v || "") || v;

export const formatPhone = (v: string) => {
  if (!v) return v;

  let r = v.replace(/\D/g, "");

  r = r.replace(/^0/, "");

  if (r?.length > 10) {
    r = r.replace(/^(\d\d)(\d{5})(\d{4}).*/, "($1) $2-$3");
  }

  return r;
};

export const phoneValidate = (phone: string) => {
  const regex = /^\(\d{2}\) \d{5}-\d{4}$/;
  if (!regex.test(phone)) {
    return false;
  }
  const onlyNumbers = phone.replace(/\D/g, "");
  const allEquals = onlyNumbers.split("").every((d) => d === onlyNumbers[0]);
  if (allEquals) {
    return false;
  }

  return true;
};

export const maskCep = (value: string) =>
  value
    .replace(/\D/g, "")
    .replace(/^(\d{5})(\d)/, "$1-$2")
    .slice(0, 9);

export const unMask = (v: string) => {
  if (!v) return v;

  return v.replace(/[^a-zA-Z0-9]/g, "");
};

export const formatRole = (v: string) => {
  if (v === "MASTER") return "Proprietário";
  if (v === "PROFESSOR") return "Professor";
  if (v === "STUDENT") return "Estudante";
  if (v === "RESPONSABLE") return "Responsável";
  if (v === "STORE_ADMIN") return "Administrativo";
  if (v === "BACKOFFICE") return "Backoffice";
};

export const formatRecurrence = (v: string) => {
  if (v === "MONTHLY") return "Mensal";
  if (v === "QUARTERLY") return "Trimestral";
  if (v === "SEMIANNUAL") return "Semestral";
  if (v === "YEARLY") return "Anual";
};

export const formatPaymentType = (v: string) => {
  if (v === "CREDIT_CARD") return "Cartão de credito";
  if (v === "PIX") return "Pix";
  if (v === "PIX_BILLET") return "Pix";
  if (v === "DEBIT_ACCOUNT") return "Débito em conta";

  return v;
};

export const formatPaymentStatus = (v: string) => {
  if (v === "PAID") return "Pago";
  if (v === "CONFIRMED") return "Confirmado";
  if (v === "CONFIRMED_INSTALLMENT") return "Confirmado";

  if (v === "PENDING") return "Em aberto";
  if (v === "OVERDUE") return "Atrasada";
  if (v === "RECEIVED") return "Recebida";
  if (v === "RECEIVED_IN_CASH") return "Recebimento em dinheiro";
  if (v === "ERROR") return "Pagamento não autorizado";
  if (v === "CANCELED") return "Cancelada";
  if (v === "SCHEDULED") return "Agendada";
  if (v === "DEBITED") return "Débito em conta";

  return v;
};

export const getColorByPaymentStatus = (v: string) => {
  if (v === "PAID") return "success";
  if (v === "CONFIRMED") return "success";
  if (v === "CONFIRMED_INSTALLMENT") return "success";
  if (v === "PENDING") return "warning";
  if (v === "OVERDUE") return "error";
  if (v === "RECEIVED") return "success";
  if (v === "RECEIVED_IN_CASH") return "success";
  if (v === "ERROR") return "error";
  if (v === "CANCELED") return "error";
  if (v === "SCHEDULED") return "";
  if (v === "DEBITED") return "error";

  return v;
};

export const cpfCnpjValidator = (v: string) => {
  return cpf.isValid(v) || cnpj.isValid(v);
};

export const maskMoney = (text: number | string) => {
  text = text?.toString();

  const cleanedText = text?.replace(/[^\d]/g, "");

  let formattedAmount = "";

  if (cleanedText?.length === 0) {
    formattedAmount = "0,00";
  } else if (cleanedText?.length === 1) {
    formattedAmount = `0,0${cleanedText}`;
  } else {
    const integerPart = cleanedText?.slice(0, -2);
    const decimalPart = cleanedText?.slice(-2);
    formattedAmount = `${integerPart?.replace(/^0+/, "")},${decimalPart}`;
  }

  return formattedAmount;
};

export const unMaskMoney = (text: any) => {
  if (!text) return text;

  text = text.toString();
  const cleanedText = text?.replace(",", ".");
  return Number(cleanedText).toFixed(2);
};

export const createFileObject = (uri: any) => {
  if (!uri) return "";

  let fileName = uri.substring(uri.lastIndexOf("/") + 1);
  let extension = fileName?.split(".").pop().toLowerCase();
  let fileType;

  switch (extension) {
    case "jpg":
      fileType = "image/jpg";
      break;
    case "jpeg":
      fileType = "image/jpeg";
      break;
    case "png":
      fileType = "image/png";
      break;
    default:
      fileType = "application/octet-stream";
  }

  return {
    uri: uri,
    name: fileName,
    type: fileType,
  };
};

export const isColorLight = (hex: string) => {
  if (!hex) return "";
  // Convert hex to RGB
  const hexColor = hex.replace("#", "");
  const r = parseInt(hexColor.substring(0, 2), 16);
  const g = parseInt(hexColor.substring(2, 4), 16);
  const b = parseInt(hexColor.substring(4, 6), 16);

  // Calculate brightness
  const brightness = 0.2126 * r + 0.7152 * g + 0.0722 * b;

  // Return true if light, false if dark
  return brightness > 128;
};

export const formatCreditCard = (value: string) => {
  const v = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");

  const matches = v.match(/\d{4,16}/g);
  const match = (matches && matches[0]) || "";
  const parts = [];

  for (let i = 0, len = match?.length; i < len; i += 4) {
    parts.push(match.substring(i, i + 4));
  }

  return parts?.length ? parts.join(" ") : value;
};

export const formatExpiryDate = (value: string) => {
  const v = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");

  // Only consider up to 4 digits for MM/YY format
  const match = v.substring(0, 4);

  let parts = [];

  if (match?.length >= 2) {
    parts.push(match.substring(0, 2)); // MM
  }

  if (match?.length > 2) {
    parts.push(match.substring(2, 4)); // YY
  }
  // Join the parts with '/'
  return parts?.length ? parts.join("/") : value;
};

export const formatCvc = (value: string) => {
  const v = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");

  if (v.length >= 3) {
    return v.substring(0, 3);
  } else {
    return v;
  }
};

export const foarmtPaymentStatus = (v: any) => {
  if (v === "PAID") return "Paga";
  if (v === "CONFIRMED") return "Confirmada";
  if (v === "PENDING") return "Em aberto";
  if (v === "OVERDUE") return "Atrasada";
  if (v === "RECEIVED") return "Recebida";
  if (v === "RECEIVED_IN_CASH") return "Recebimento em dinheiro";
  if (v === "ERROR") return "Pagamento não autorizado";
  if (v === "CANCELED") return "Cancelada";
  if (v === "SCHEDULED") return "Agendada";

  return v;
};

export const foarmtPaymentType = (v: any) => {
  if (v === "CREDIT_CARD") return "Cartão de credito";
  if (v === "PIX") return "Pix";
  if (v === "PIX_BILLET") return "Pix";
  if (v === "DEBIT_ACCOUNT") return "Débito em conta";

  return v;
};

export const formatNumber = (v: string | number) => {
  if (!v) return String(v);

  v = String(v);

  let r = v.replace(/\D/g, "");

  r = r.replace(/^0/, "");

  return r;
};
